import { useTranslation } from "react-i18next";
import { TextAlignOption } from "@/components/canvas/types/textTypes";
import { TextDecoration } from "./TextInspector";
import { Separator } from "@/components/ui/separator";
// import { TransformOptions } from "./shared/TransformOptions";
import { Delete } from "./shared/Delete";
import { Block } from "./shared/Block";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  BoldIcon,
  ItalicIcon,
  StrikethroughIcon,
  UnderlineIcon,
} from "lucide-react";
import { BlockSection } from "./shared/BlockSection";
import { ColorPicker } from "./shared/ColorPicker";
import { Slider } from "@/components/ui/slider";
import { FONT_FAMILIES_LIST } from "@/components/canvas/constants/canvasConstants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { LayerOption } from "./shared/LayerOption";
import { ChangeLayerFunction } from "@/components/canvas/extensions/layerManagerExtension";

export type TextInspectorContentProps = {
  fillOption: {
    value: string;
    onChange: (value: string) => void;
  };
  fontSizeOption: {
    value: number;
    onChange: (value: number) => void;
  };
  fontFamilyOption: {
    value: string;
    onChange: (value: string) => void;
  };
  opacityOption: {
    value: number;
    onChange: (value: number) => void;
  };
  strokeColorOption: {
    value: string;
    onChange: (value: string) => void;
  };
  strokeWidthOption: {
    value: number;
    onChange: (value: number) => void;
  };
  textAlignOption: {
    value: TextAlignOption;
    onChange: (value: TextAlignOption) => void;
  };
  xPositionOption: {
    value: number;
    onChange: (value: number) => void;
  };
  yPositionOption: {
    value: number;
    onChange: (value: number) => void;
  };
  widthOption: {
    value: number;
    onChange: (value: number) => void;
  };
  rotationOption: {
    value: number;
    onChange: (value: number) => void;
  };
  textDecorationsOption: {
    value: TextDecoration[];
    onChange: (value: TextDecoration[]) => void;
  };
  //
  handleLayerChange: ChangeLayerFunction;
  onDeleteElementClick: () => void;
};

export const TextInspectorContent: React.FC<TextInspectorContentProps> = ({
  fillOption,
  fontSizeOption,
  fontFamilyOption,
  opacityOption,
  strokeColorOption,
  strokeWidthOption,
  textAlignOption,
  // xPositionOption,
  // yPositionOption,
  // widthOption,
  // rotationOption,
  handleLayerChange,
  textDecorationsOption,
  onDeleteElementClick,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Block title={t("template.inspector.fontSettings")}>
        {/* Decoration */}
        <BlockSection title={t("template.inspector.decoration")}>
          <ToggleGroup
            type="multiple"
            onValueChange={(value) => textDecorationsOption.onChange(value as TextDecoration[])}
            value={textDecorationsOption.value}
            className="justify-start"
          >
            <ToggleGroupItem value="bold">
              <BoldIcon />
            </ToggleGroupItem>
            <ToggleGroupItem value="italic">
              <ItalicIcon />
            </ToggleGroupItem>
            <ToggleGroupItem value="underline">
              <UnderlineIcon />
            </ToggleGroupItem>
            <ToggleGroupItem value="striked">
              <StrikethroughIcon />
            </ToggleGroupItem>
          </ToggleGroup>
        </BlockSection>
        {/* Alignment */}
        <BlockSection title={t("template.inspector.textAlign")}>
          <ToggleGroup
            type="single"
            onValueChange={(value) => textAlignOption.onChange(value as TextAlignOption)}
            value={textAlignOption.value}
            className="justify-start"
          >
            <ToggleGroupItem value="left">
              <AlignLeft />
            </ToggleGroupItem>
            <ToggleGroupItem value="center">
              <AlignCenter />
            </ToggleGroupItem>
            <ToggleGroupItem value="right">
              <AlignRight />
            </ToggleGroupItem>
          </ToggleGroup>
        </BlockSection>
        {/* Font family and size */}
        <BlockSection title={t("template.inspector.textStyle")}>
          <Select
            onValueChange={(value) => {
              fontFamilyOption.onChange(value);
            }}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder={t("template.inspector.fontFamily")} />
            </SelectTrigger>
            <SelectContent>
              {FONT_FAMILIES_LIST.map((fontFamily) => {
                return (
                  <SelectItem key={fontFamily} value={String(fontFamily)} style={{ fontFamily }}>
                    {fontFamily}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </BlockSection>
        {/* Font size */}
        <BlockSection title={t("template.inspector.size")}>
          <Input
            value={fontSizeOption.value}
            onChange={(e) => fontSizeOption.onChange(Number(e.target.value))}
            min={1}
            className="w-[80px]"
            type="number"
          />
        </BlockSection>
      </Block>
      <Separator />
      {/* Fill */}
      <Block title={t("template.inspector.fill")}>
        <BlockSection title={t("template.inspector.color")}>
          <ColorPicker color={fillOption.value} onChange={fillOption.onChange} />
        </BlockSection>
        <BlockSection title={t("template.inspector.opacity")}>
          <Slider
            value={[opacityOption.value]}
            onValueChange={(value) => opacityOption.onChange(value[0])}
            step={0.1}
            max={1}
          />
        </BlockSection>
      </Block>
      <Separator />
      {/* Stroke */}
      <Block title={t("template.inspector.stroke")}>
        <BlockSection title={t("template.inspector.color")}>
          <ColorPicker color={strokeColorOption.value} onChange={strokeColorOption.onChange} />
        </BlockSection>
        <BlockSection title={t("template.inspector.width")}>
          <Slider
            value={[strokeWidthOption.value]}
            onValueChange={(value) => strokeWidthOption.onChange(value[0])}
            step={1}
            max={100}
          />
        </BlockSection>
      </Block>
      <Separator />
      {/* Layer */}
      <LayerOption handleLayerChange={handleLayerChange} />
      {/* Transform */}
      {/* <TransformOptions
        rotationOption={rotationOption}
        widthOption={widthOption}
        yPositionOption={yPositionOption}
        xPositionOption={xPositionOption}
      /> */}
      <Separator />
      {/* Delete */}
      <Delete onClick={onDeleteElementClick} />
    </>
  );
};
